﻿import ko from 'knockout';
import 'knockout.validation';
import MicroModal from 'micromodal';
import { httpClient, HTTPClientError } from './httpClient';
import {
	CancellationMessage,
	DeleteMeetingRequest,
	StatusError,
} from './interfaces';
import { initPhone } from './form-helpers';
import { Log } from './log';
import { PhoneViewModel } from './phone-view-model';
import { R } from './Resources.resx';

class LogListModalViewModel {
	public selectedMeetingId: number;
	private selectedInviteMeetingId: number | null = null;

	public isSubmitting = ko.observable(false);
	public daSubject = ko.observable<string>();
	public daMessage = ko.observable<string>();
	public klSubject = ko.observable<string>();
	public klMessage = ko.observable<string>();
	public cancellationMessageLanguage = ko.observable<'da' | 'kl'>('da');
	public languageWhenOpeningModal = ko.observable<'da' | 'kl'>('da');

	public inviteErrors = ko.observable<string[]>([]);
	public inviteEmail = ko.observable<string>('');
	public invitePhone = ko.observable<PhoneViewModel>(new PhoneViewModel());

	private getLanguageFromCookies(): string | null {
		const cookies = document.cookie.split(';');
		const cultureCookie = cookies.find((cookie) =>
			cookie.trim().startsWith('.AspNetCore.Culture='),
		);

		if (cultureCookie) {
			const language = cultureCookie.split('uic=')[1];
			return language?.trim() || null;
		}

		return null;
	}
	public setCancellationMessageLanguage(language: 'da' | 'kl') {
		this.cancellationMessageLanguage(language);
	}
	public confirmDelete(meetingId: number): void {
		this.selectedMeetingId = meetingId;

		const language = this.getLanguageFromCookies();
		if (language === 'da' || language === 'kl') {
			this.languageWhenOpeningModal(language);
			this.cancellationMessageLanguage(language);
		}

		httpClient
			.get(`/api/meetings/default-cancellation-message?meetingId=${meetingId}`)
			.then((data: CancellationMessage) => {
				this.daSubject(data.daSubject);
				this.daMessage(data.daMessage);
				this.klSubject(data.klSubject);
				this.klMessage(data.klMessage);
			});

		MicroModal.show('delete-meeting-confirm', {
			awaitCloseAnimation: true,
			awaitOpenAnimation: false,
			disableFocus: true,
		});
	}

	public deleteMeeting(): void {
		this.isSubmitting(true);
		const request: DeleteMeetingRequest = {
			meetingId: this.selectedMeetingId,
			daSubject: this.daSubject(),
			daMessage: this.daMessage(),
			klSubject: this.klSubject(),
			klMessage: this.klMessage(),
		};

		httpClient
			.post('/api/meetings/delete', request)
			.catch((error) => {
				if (error instanceof HTTPClientError) {
					// The request was made and the server responded with a status code
					// that falls out of the range of 2xx
					Log.error(
						'Error deleting meeting. ' +
							error.status +
							': ' +
							(error.data ?? error.message),
					);
				} else {
					// Something happened in setting up the request that triggered an Error
					Log.error('Error deleting meeting', error.message);
				}
			})
			.finally(() => {
				window.location.reload();
			});
	}

	constructor() {
		this.inviteEmail.extend({
			email: {
				onlyIf: () => this.inviteEmail().trim().length > 0,
				message: R.ErrorInvalidEmail,
			},
		});
	}

	public showInviteDialog(meetingId: number): void {
		this.selectedInviteMeetingId = meetingId;
		MicroModal.show('inviteDialog', {
			awaitCloseAnimation: true,
			disableFocus: true,
		});
	}

	public async sendInvite(): Promise<void> {
		const emailValid = this.inviteEmail.isValid();
		if (!emailValid) {
			return;
		}

		if (!this.inviteEmail() && !this.invitePhone().isValid()) {
			this.inviteErrors([R.ErrorEmailAndOrPhone]);
			return;
		}

		try {
			const response = await fetch(
				'/api/meetings/invite/' + this.selectedInviteMeetingId,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						email: this.inviteEmail().trim(),
						phone: this.invitePhone().isValid()
							? this.invitePhone().value().trim()
							: null,
					}),
				},
			);

			const body = await response.json();
			if (response.ok && body.hasErrors) {
				const errors: string[] = body.errors.map(
					(err: StatusError) => err.value,
				);
				this.inviteErrors(errors);
				return;
			}

			this.inviteErrors([]);
			MicroModal.close('inviteDialog');
		} catch (error) {
			Log.error(error);
			this.inviteErrors([R.ErrorUnknown]);
		}
	}
}

export function LogList(): void {
	ko.applyBindings(
		new LogListModalViewModel(),
		document.getElementById('log-list'),
	);
	initPhone('#inviteSms');
}
